import z from "zod";

/**
 * What belongs in these schemas.
 *
 * A field a caller reads is required, so a parse failure beats an `undefined`
 * reaching a redirect. A field nobody reads and nobody plausibly will is left
 * out — Zod strips unknown keys, and a required field with no reader turns any
 * rename of it into a dead page. A field nobody reads yet but a billing
 * surface will obviously want is optional: present in the contract, not yet
 * load-bearing, and promoted to required by whoever renders it.
 *
 * Request bodies below are exempt — this side writes them, so every field is
 * read by definition and the validation runs before a call rather than after.
 */

/**
 * A rooted path inside the app, which is all a caller may name as a checkout or
 * portal destination. The server owns the host and builds the absolute URL, so
 * a payment flow cannot be aimed off-site. A protocol-relative path carries an
 * authority and is rejected for the same reason an absolute URL is.
 */
const ReturnPathSchema = z
  .string()
  .startsWith("/")
  .refine((path) => !path.startsWith("//"), { message: "must not be protocol-relative" });

export const CreateCheckoutInputSchema = z.object({
  productType: z.string(),
  successPath: ReturnPathSchema,
  cancelPath: ReturnPathSchema,
  quantity: z.number().int().optional(),
});

// The session id and its expiry are dropped: every caller redirects to the URL
// the moment it arrives, so there is no link left around to expire.
export const CheckoutSessionResponseSchema = z.object({
  checkoutUrl: z.string(),
});

export const CreatePortalInputSchema = z.object({
  returnPath: ReturnPathSchema,
});

// The return URL is the server echoing back the path this side sent, and the
// expiry has the same non-reader as the checkout's.
export const PortalSessionResponseSchema = z.object({
  portalUrl: z.string(),
});

/**
 * Staff-set terms that override the plan's price, refill, rollover and top-up
 * rate while its kind still decides features. `refillCredits` is 0 when the
 * organization has no refill schedule, and `topUpMultiplier` replaces the plan's
 * top-up bonus: 1 is list price, 2 is half price.
 *
 * `collectionMethod` and `nextInvoiceAt` are absent while the plan is billed
 * outside Stripe. The method is a plain string so an unknown value doesn't fail
 * the whole subscription response; readers narrow it and show nothing for a
 * value they don't know.
 */
const CustomTermsSchema = z.object({
  label: z.string(),
  priceCents: z.number(),
  billingPeriod: z.enum(["monthly", "yearly"]),
  refillCredits: z.number(),
  refillCadence: z.string().nullish(),
  creditExpiryDays: z.number(),
  topUpMultiplier: z.number(),
  nextRefillAt: z.string().optional(),
  collectionMethod: z.string().nullish(),
  nextInvoiceAt: z.string().nullish(),
});
export type CustomTerms = z.infer<typeof CustomTermsSchema>;

/**
 * The organization's subscription, or the free tier when it holds none.
 *
 * `canManageBilling` decides whether the settings page offers the portal, and
 * `customTerms` is absent for an organization on catalog terms. The rest are
 * optional rather than dropped: a billing surface that never shows its state,
 * its renewal date or a failed payment is one that is not finished. The
 * provider's own id and the product code are dropped instead; neither names
 * anything a person would read on a page.
 */
const SubscriptionSchema = z.object({
  canManageBilling: z.boolean(),

  planType: z.string().optional(),
  planDisplayName: z.string().optional(),
  status: z.string().optional(),
  billingInterval: z.string().optional(),
  currentPeriodEnd: z.string().optional(),
  cancelAt: z.string().optional(),
  isDelinquent: z.boolean().optional(),
  customTerms: CustomTermsSchema.optional(),
});
export type Subscription = z.infer<typeof SubscriptionSchema>;

export const SubscriptionResponseSchema = z.object({
  subscription: SubscriptionSchema.nullable(),
});

// The only plan an organization can move to on its own; every other change
// goes through checkout or support.
const UpgradeTargetSchema = z.literal("poweruser");

export const UpgradePreviewParamsSchema = z.object({
  target: UpgradeTargetSchema,
});

/**
 * `amountDueCents` is prorated to the moment of the request, so it drifts while
 * the preview sits open. `creditsNow` lands on confirm and `newPeriodCredits` on
 * every renewal from `nextRenewalAt`.
 */
const UpgradePreviewSchema = z.object({
  billingInterval: z.enum(["month", "year"]),
  amountDueCents: z.number(),
  currency: z.string(),
  creditsNow: z.number(),
  newPeriodCredits: z.number(),
  nextRenewalAt: z.string(),
});
export type UpgradePreview = z.infer<typeof UpgradePreviewSchema>;

export const UpgradePreviewResponseSchema = z.object({
  preview: UpgradePreviewSchema,
});

export const UpgradeInputSchema = z.object({
  target: UpgradeTargetSchema,
});

export const UpgradeResponseSchema = z.object({
  subscription: SubscriptionSchema,
});

/**
 * `card` is the payer's saved card and `isPayer` says whether that payer is the
 * caller; saving a new card makes the caller the payer. `lastFailure.message`
 * is written for a person to read. A failed charge turns the top-up off with
 * status `needs_attention` until an owner turns it back on.
 */
const AutoTopUpSchema = z.object({
  enabled: z.boolean(),
  status: z.enum(["off", "active", "needs_attention"]),
  thresholdCredits: z.number(),
  amountCents: z.number(),
  monthlyCapCents: z.number(),
  spentThisMonthCents: z.number(),
  card: z.object({ brand: z.string(), last4: z.string() }).nullish(),
  isPayer: z.boolean(),
  payerName: z.string().nullish(),
  lastFailure: z.object({ message: z.string() }).nullish(),
});
export type AutoTopUp = z.infer<typeof AutoTopUpSchema>;

export const AutoTopUpResponseSchema = z.object({
  autoTopUp: AutoTopUpSchema,
});

export const UpdateAutoTopUpInputSchema = z.object({
  enabled: z.boolean(),
  thresholdCredits: z.number().int(),
  amountCents: z.number().int(),
  monthlyCapCents: z.number().int(),
});

// A rooted path despite the field name; the server supplies the host.
export const StartAutoTopUpCardInputSchema = z.object({
  returnUrl: ReturnPathSchema,
});

/** One row of the purchase picker, across every organization the caller reaches. */
export const FundableOrganizationSchema = z.object({
  organizationId: z.string(),
  displayName: z.string(),
  canFund: z.boolean(),
  planType: z.string(),
  canManageSubscription: z.boolean(),
});
export type FundableOrganization = z.infer<typeof FundableOrganizationSchema>;

export const FundableOrganizationsResponseSchema = z.object({
  organizations: z
    .array(FundableOrganizationSchema)
    .nullable()
    .transform((value) => value ?? []),
});
